import { registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import localeEn from "@angular/common/locales/en";
import localeEs from "@angular/common/locales/es";
import localePt from "@angular/common/locales/pt";
import { APP_INITIALIZER, LOCALE_ID, NgModule } from "@angular/core";
import { AngularFireModule, FIREBASE_OPTIONS } from "@angular/fire";
import { AngularFireAuthModule } from "@angular/fire/auth";
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ServiceWorkerModule } from "@angular/service-worker";
import { EdpBannerAppModule } from "@edp-components/edp-banner-app/edp-banner-app.module";
import { EdpModalIframeModule } from "@edp-components/edp-modal-iframe/edp-modal-iframe.module";
import { EdpRatingModule } from "@edp-components/edp-rating/edp-rating.module";
import { SharedModule } from "@edp-components/shared.module";
import { environment } from "@edp-environments/environment";
import { EdpAuthInterceptor } from "@edp-interceptors/auth.interceptor";
import { ErrorHandlingInterceptorProvider } from "@edp-interceptors/error-handling.interceptor";
import { NgbDateCustomFormatter } from "@edp-pages/edpe/accept-collective-challenge/datepicker-custom-parser-formatter";
import { LanguageService } from "@edp-services/language.service";
import { BenefitsEffects } from "@edp-store/effects/benefits.effects";
import { ChallengeEffects } from "@edp-store/effects/challenges.effects";
import { ConsumptionEffects } from "@edp-store/effects/consumption.effects";
import { DashboardEffects } from "@edp-store/effects/dashboard.effects";
import { EdpeEffects } from "@edp-store/effects/edpe.effects";
import { EnergyEfficiencyEffects } from "@edp-store/effects/energy-efficiency.effects";
import { FileUploadEffects } from "@edp-store/effects/file-upload.effects";
import { IframeEffects } from "@edp-store/effects/iframe.effects";
import { LayoutEffects } from "@edp-store/effects/layout.effects";
import { LevelsEffects } from "@edp-store/effects/levels.effects";
import { NotificationsEffects } from "@edp-store/effects/notifications.effects";
import { OperationsEffects } from "@edp-store/effects/operations.effects";
import { RatingEffects } from "@edp-store/effects/rating.effects";
import { ReadingEffects } from "@edp-store/effects/reading.effects";
import { RequestsAndOperationsEffects } from "@edp-store/effects/requests-and-operations.effects";
import { SmartNowEffects } from "@edp-store/effects/smart-now.effects";
import { SupportProjectEffects } from "@edp-store/effects/support-projects.effects";
import { TechnicalAssistancesEffects } from "@edp-store/effects/technical-assistances.effects";
import { UserEffects } from "@edp-store/effects/user.effects";
import { appReducers } from "@edp-store/reducers/app.reducers";
import { Request } from "@edp-utils/request.utils";
import { AuthJWTToken, AuthModule, SingleSignOnAuthStrategy } from "@edpc/edp-auth";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateModule } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
// import { DeviceDetectorModule } from "ngx-device-detector";
import { GuidedTourModule } from "@edp-components/ngx-guided-tour/guided-tour.module";
import { EnvService } from "@edp-services/env.service";
import { RegionService } from "@edp-services/region.service";
import { EffectsContractsModule } from "@edp-store/effects-contracts.module";
import { EsDocumentEffects } from "@edp-store/effects/ES/document-es.effects";
import { FileUploadEffectsES } from "@edp-store/effects/ES/file-upload-es.effects";
import { OrderEsEffects } from "@edp-store/effects/ES/order-es.effects";
import { EsProductEffects } from "@edp-store/effects/ES/product-es.effects";
import { RequestsOperationsESEffects } from "@edp-store/effects/ES/request-operations-es.effects";
import { SolarAccountEsEffects } from "@edp-store/effects/ES/solar-account-es.effects";
import { AssistanceRequestEffects } from "@edp-store/effects/assistance-request.effects";
import { BillingEffects } from "@edp-store/effects/billing.effects";
import { ConsumptionOmieEffects } from "@edp-store/effects/consumption-omie.effects";
import { DefineHouseEffects } from "@edp-store/effects/define-house.effects";
import { EnergyComparisonEffects } from "@edp-store/effects/energy.comparison.effects";
import { LanguageEffects } from "@edp-store/effects/language.effects";
import { OnboardingEffects } from "@edp-store/effects/onboarding.effects";
import { OpenAreaEffects } from "@edp-store/effects/open-area.effects";
import { SolarEffects } from "@edp-store/effects/solar.effects";
import { appEsReducers } from "@edp-store/reducers/ES/app-es.reducers";
import { getRegionByTLD } from "@edp-utils/region.utils";
import { DeviceDetectorService } from "ngx-device-detector";
import { NgxMaskModule } from "ngx-mask";
import { QuillModule } from "ngx-quill";
import { LayoutModule } from "./@layouts/layout/layout.module";
import { PublicLayoutModule } from "./@layouts/public-layout/public-layout.module";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

registerLocaleData(localePt, "pt");
registerLocaleData(localeEs, "es");
registerLocaleData(localeEn, "en");

export function languageProviderFactory(provider: LanguageService) {
    return () => provider.getLanguageKeys();
}
export class MyHammerConfig extends HammerGestureConfig {
    overrides = {
        swipe: { velocity: 0.4, threshold: 20 },
    } as any;
}

export function FirebaseSettingsFactory(regionService: RegionService) {
    return regionService.getFirebaseSettings();
}
export function GoogleTagManagerFactory(envService: EnvService) {
    return envService.getAnalyticsValue();
}

export function ConfigFactory(cookieService: CookieService) {
    return (): Promise<any> => {
        return new Promise<void>((resolve, reject) => {
            const isMobile: boolean = Request.getParameter("Mobile") === "1";
            const isApp: boolean = Request.getParameter("app") === "1" || cookieService.get("isOnShell") === "true";

            if (isMobile) {
                cookieService.set("IsMobile", "1", 180, "/", undefined, true, "Lax");
            } else {
                cookieService.delete("IsMobile");
            }

            if (isApp) {
                cookieService.set("isOnShell", "true", 180, "/", undefined, true, "Lax");
            }

            environment.isApp = isApp;
            environment.isMobile = cookieService.get("IsMobile") === "1";

            if (location.search) {
                sessionStorage.setItem("edponline-query", location.search);
            }

            resolve();
        });
    };
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HammerModule,
        EdpModalIframeModule,
        EdpRatingModule,
        SharedModule.forRoot(),
        StoreModule.forRoot({ ...appReducers, ...appEsReducers }),
        EffectsModule.forRoot([
            DashboardEffects,
            ConsumptionEffects,
            ReadingEffects,
            UserEffects,
            IframeEffects,
            ChallengeEffects,
            SupportProjectEffects,
            LevelsEffects,
            BenefitsEffects,
            FileUploadEffects,
            LayoutEffects,
            EdpeEffects,
            NotificationsEffects,
            RatingEffects,
            OperationsEffects,
            SmartNowEffects,
            EnergyEfficiencyEffects,
            RequestsAndOperationsEffects,
            TechnicalAssistancesEffects,
            BillingEffects,
            OpenAreaEffects,
            SolarEffects,
            LanguageEffects,
            // ----- ES -----
            OrderEsEffects,
            SolarAccountEsEffects,
            RequestsOperationsESEffects,
            EsDocumentEffects,
            EsProductEffects,
            FileUploadEffectsES,
            // ----- END ES -----
            AssistanceRequestEffects,
            OnboardingEffects,
            ConsumptionOmieEffects,
            DefineHouseEffects,
            EnergyComparisonEffects,
        ]),
        // load contracts effects by region
        EffectsContractsModule,
        AngularFireModule,
        AngularFireAuthModule,
        AppRoutingModule,
        ServiceWorkerModule.register("ngsw-worker.js", { enabled: environment.production }),
        HttpClientModule,
        NgbModule,
        LayoutModule,
        PublicLayoutModule,
        EdpBannerAppModule,
        TranslateModule.forRoot(),
        // DeviceDetectorService.forRoot(),
        NgxMaskModule.forRoot(),
        AuthModule.forRoot({
            strategies: [
                SingleSignOnAuthStrategy.setup({
                    name: "sso",
                    baseEndpoint: "/api",
                    token: {
                        endpoint: "pt.edp.sso.firebase.validateToken",
                        requireValidToken: true,
                        class: AuthJWTToken,
                    },
                    authorize: {
                        redirectUri:
                            Request.getParameter("app") === "1"
                                ? `${environment.ssoUrl[getRegionByTLD()]}/?Mobile=1`
                                : environment.ssoUrl[getRegionByTLD()],
                    },
                    revoke: {
                        endpoint: "pt.edp.sso.firebase.revokeToken",
                    },
                    bootstrap: false,
                }),
            ],
        }),
        QuillModule.forRoot({
            modules: {
                syntax: false,
            },
        }),
        !environment.production ? StoreDevtoolsModule.instrument() : [],
        GuidedTourModule,
    ],
    bootstrap: [AppComponent],
    providers: [
        ErrorHandlingInterceptorProvider,
        CookieService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: EdpAuthInterceptor,
            multi: true,
        },
        LanguageService,
        {
            provide: APP_INITIALIZER,
            useFactory: languageProviderFactory,
            deps: [LanguageService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: ConfigFactory,
            deps: [CookieService],
            multi: true,
        },
        {
            provide: LOCALE_ID,
            deps: [CookieService],
            useFactory: cookie => cookie.get("user_language"),
        },
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig,
        },
        RegionService,
        {
            provide: FIREBASE_OPTIONS,
            useFactory: FirebaseSettingsFactory,
            deps: [RegionService],
            multi: false,
        },
        EnvService,
        {
            provide: "googleTagManagerId",
            deps: [EnvService],
            useFactory: GoogleTagManagerFactory,
        },
        NgbDateCustomFormatter,
        DeviceDetectorService,
    ],
})
export class AppModule { }
